import React from 'react';
import { View, StyleSheet } from 'react-native';
import { useDispatch } from 'react-redux';
import { DLOVolumeForecast, DLOPercentageOfWeeklyDSDVolumeTable } from '../../../components/Tables';
import DSDVolumeZoneDistributionByDay from '../../../components/Tables/DSDVolumeZoneDistributionByDay';
import NonDSDVolumeZoneDistributionByDay from '../../../components/Tables/NonDSDVolumeZoneDistributionByDay';
import { HistoricalHoursByDayAverage } from '../../../components/Tables';
import DLOTable from '../../../components/Tables/DLOTable';
import LoadingTableContainer from './LoadingTableContainer';
import { 
  setScheduleForecastTableData, 
  setForecastDSDTableData, 
  setForecastNONDSDTableData, 
  setForecastDSDZoneTableData, 
  setForecastNONDSDZoneTableData, 
  setHistoricalHoursTableData 
} from '../../../../../store/slices/dloSlice';
import { transformDailyPercentageData, transformHistoricalHoursData, transformZoneDistributionData } from '../utils/dataTransformers';

interface ForecastedVolumeTabProps {
  // Data
  scheduleForecastData: any;
  scheduleForecastTableData: any[];
  scheduleForecastLoading: boolean;
  dloScheduleSetting: any;
  forecastDSDTableData: any[];
  forecastNONDSDTableData: any[];
  forecastDSDZoneTableData: any[];
  forecastNONDSDZoneTableData: any[];
  historicalHoursTableData: any[];
  historicalHoursData: any;
  historicalHoursLoading: boolean;
  shiftTimesLoading: boolean;
  
  // Loading states
  forecastDSDUpdateLoading: boolean;
  forecastNONDSDUpdateLoading: boolean;
  forecastDSDZoneUpdateLoading: boolean;
  forecastNONDSDZoneUpdateLoading: boolean;
  historicalHoursUpdateLoading: boolean;
  shiftTimesUpdateLoading: boolean;
  
  // Saved states
  forecastDSDZoneSaved: boolean;
  forecastNONDSDZoneSaved: boolean;
  
  // Handlers
  handlers: any;
  
  // Styles
  styles: any;
}

const ForecastedVolumeTab: React.FC<ForecastedVolumeTabProps> = ({
  scheduleForecastData,
  scheduleForecastTableData,
  scheduleForecastLoading,
  dloScheduleSetting,
  forecastDSDTableData,
  forecastNONDSDTableData,
  forecastDSDZoneTableData,
  forecastNONDSDZoneTableData,
  historicalHoursTableData,
  historicalHoursData,
  historicalHoursLoading,
  shiftTimesLoading,
  forecastDSDUpdateLoading,
  forecastNONDSDUpdateLoading,
  forecastDSDZoneUpdateLoading,
  forecastNONDSDZoneUpdateLoading,
  historicalHoursUpdateLoading,
  forecastDSDZoneSaved,
  forecastNONDSDZoneSaved,
  handlers,
  styles,
}) => {
  const dispatch = useDispatch();

  return (
    <View style={localStyles.tabPanel}>
      {/* 1. Volume Forecast Table */}
      {scheduleForecastLoading ? (
        <LoadingTableContainer title="Volume Forecast" style={styles.tableSpacing} />
      ) : (
        <DLOVolumeForecast
          data={scheduleForecastData?.data}
          value={scheduleForecastTableData}
          onChange={(data) => dispatch(setScheduleForecastTableData(data))}
          onSave={handlers.handleSaveVolumeForecast}
          onRowSave={handlers.handleRowSaveVolumeForecast}
          onRowClear={handlers.handleRowClearVolumeForecast}
          onRowRestoreDefaults={handlers.handleRowRestoreDefaultsVolumeForecast}
          style={styles.tableSpacing}
          isLoading={false}
        />
      )}

      {/* 2. Percentage of Weekly DSD Volume */}
      {dloScheduleSetting.forecastDailyLoading ? (
        <LoadingTableContainer title="Percentage of Weekly DSD Volume" style={styles.tableSpacing} />
      ) : (
        <DLOPercentageOfWeeklyDSDVolumeTable
          data={transformDailyPercentageData(dloScheduleSetting.scheduleSettingsForecastDaily?.data || [])}
          value={forecastDSDTableData}
          onChange={(data) => dispatch(setForecastDSDTableData(data))}
          onSave={handlers.handleSaveDSDPercentage}
          onRowSave={(rowId) => console.log('DSD Percentage row save action:', rowId)}
          onRowRestoreDefaults={(rowId) => console.log('DSD Percentage row restore defaults action:', rowId)}
          headerTitle="PERCENTAGE OF WEEKLY DSD VOLUME"
          type="DSD"
          context="schedule-settings"
          style={styles.tableSpacing}
          isLoading={forecastDSDUpdateLoading}
        />
      )}

      {/* 3. DSD Zone Volume Distribution By Day */}
      {dloScheduleSetting.forecastZoneDistributionDSDLoading ? (
        <LoadingTableContainer title="DSD Zone Volume Distribution By Day" style={styles.tableSpacing} />
      ) : (
        <DSDVolumeZoneDistributionByDay
          data={forecastDSDZoneTableData.length > 0 ? [] : transformZoneDistributionData(dloScheduleSetting.scheduleSettingsForecastZoneDistributionDSD?.data || [], 'DSD')}
          value={forecastDSDZoneTableData}
          onChange={(data) => dispatch(setForecastDSDZoneTableData(data))}
          onAddZone={() => console.log('Add zone action')}
          onSave={handlers.handleSaveDSDZoneDistribution}
          title="DSD ZONE VOLUME DISTRIBUTION BY DAY"
          type="DSD"
          context="schedule-settings"
          style={styles.tableSpacing}
          isLoading={forecastDSDZoneUpdateLoading}
          isSaved={forecastDSDZoneSaved}
          isActualVolumeTab={false}
        />
      )}

      {/* 4. Percentage of Weekly NON DSD Volume */}
      {/* Commenting out Weekly NON DSD Volume Table */}
      {/* {dloScheduleSetting.forecastDailyNONDSDLoading ? (
        <LoadingTableContainer title="Percentage of Weekly NON DSD Volume" style={styles.tableSpacing} />
      ) : (
        <DLOPercentageOfWeeklyDSDVolumeTable
          data={transformDailyPercentageData(dloScheduleSetting.scheduleSettingsForecastDailyNONDSD?.data || [])}
          value={forecastNONDSDTableData}
          onChange={(data) => dispatch(setForecastNONDSDTableData(data))}
          onSave={handlers.handleSaveNONDSDPercentage}
          onRowSave={(rowId) => console.log('NON-DSD Percentage row save action:', rowId)}
          onRowClear={(rowId) => console.log('NON-DSD Percentage row clear action:', rowId)}
          onRowDelete={(rowId) => console.log('NON-DSD Percentage row delete action:', rowId)}
          onRowRestoreDefaults={(rowId) => console.log('NON-DSD Percentage row restore defaults action:', rowId)}
          headerTitle="PERCENTAGE OF WEEKLY NON DSD VOLUME"
          type="NON-DSD"
          context="schedule-settings"
          style={styles.tableSpacing}
          isLoading={forecastNONDSDUpdateLoading}
        />
      )} */}

      {/* 5. NON DSD Zone Volume Distribution By Day */}
      {dloScheduleSetting.forecastZoneDistributionNONDSDLoading ? (
        <LoadingTableContainer title="NON DSD Zone Volume Distribution By Day" style={styles.tableSpacing} />
      ) : (
        <NonDSDVolumeZoneDistributionByDay
          data={forecastNONDSDZoneTableData.length > 0 ? [] : transformZoneDistributionData(dloScheduleSetting.scheduleSettingsForecastZoneDistributionNONDSD?.data || [], 'NON-DSD')}
          value={forecastNONDSDZoneTableData}
          onChange={(data) => dispatch(setForecastNONDSDZoneTableData(data))}
          onAddZone={() => console.log('Add NON-DSD zone action')}
          onSave={handlers.handleSaveNONDSDZoneDistribution}
          title="NON DSD ZONE VOLUME DISTRIBUTION BY DAY"
          type="NON-DSD"
          context="schedule-settings"
          style={styles.tableSpacing}
          isLoading={forecastNONDSDZoneUpdateLoading}
          isSaved={forecastNONDSDZoneSaved}
        />
      )}

      {/* 6. Historical Hours By Day Average */}
      {historicalHoursLoading ? (
        <LoadingTableContainer title="Historical Hours By Day Average" style={styles.tableSpacing} />
      ) : (
        <HistoricalHoursByDayAverage
          data={transformHistoricalHoursData(historicalHoursData?.data || historicalHoursData || [])}
          value={historicalHoursTableData}
          onChange={(data: any) => dispatch(setHistoricalHoursTableData(data))}
          onSave={handlers.handleSaveHistoricalHours}
          onAddZone={handlers.handleAddZoneHistoricalHours}
          onZoneAdded={handlers.handleZoneAddedHistoricalHours}
          onRowSave={handlers.handleSaveHistoricalHoursRow}
          onRowClear={handlers.handleClearHistoricalHoursRow}
          onRowRestore={handlers.handleRestoreHistoricalHoursRow}
          isLoading={historicalHoursUpdateLoading}
          context="schedule-settings"
          style={styles.tableSpacing}
        />
      )}

      {/* 7. DLO Table - Shift Times */}
      {shiftTimesLoading ? (
        <LoadingTableContainer title="Shift Times" style={styles.tableSpacing} />
      ) : (
        <DLOTable
          onSave={handlers.handleSaveShiftTimes}
          onClear={handlers.handleClearShiftTimes}
          data={(() => {
            console.log('DLOTable shift times data:', dloScheduleSetting.shiftTimes);
            return dloScheduleSetting.shiftTimes;
          })()}
          loading={shiftTimesLoading}
          error={shiftTimesLoading ? null : dloScheduleSetting.error}
          onRetry={handlers.retryShiftTimes}
          style={styles.tableSpacing}
        />
      )}
    </View>
  );
};

const localStyles = StyleSheet.create({
  tabPanel: {
    flex: 1,
  },
});

export default ForecastedVolumeTab;
